import '@ui5/webcomponents-icons/dist/accept';
import '@ui5/webcomponents-icons/dist/comment';
import '@ui5/webcomponents-icons/dist/goal';
import '@ui5/webcomponents-icons/dist/question-mark';
import '@ui5/webcomponents-icons/dist/sys-find';
import '@ui5/webcomponents-icons/dist/add';
import React from 'react';
import { Tag, TimelineItem } from '@ui5/webcomponents-react';
import TagDesign from '@ui5/webcomponents/dist/types/TagDesign.js';
import { CommentType, QueryComment, UserSupplierLanguages } from 'generated/graphql';
import { useTranslation } from 'react-i18next';
import { useStyles } from './CustomTimeline.jss';
import { useGetDate } from 'hooks/useGetDate';
import { CommentText } from './CommentText/CommentText';

interface CustomTimelineProps {
  comments?: QueryComment[];
  isInternalUser: boolean;
}

export const CustomTimeline: React.FC<CustomTimelineProps> = (props) => {
  const { comments, isInternalUser } = props;

  const { t } = useTranslation();
  const classes = useStyles();
  const { getFormattedDateString } = useGetDate();
  const NUM_MAX_CARACTERES = 1000;
  const NUM_MAX_LINES = 8;

  const TimelineItemClass: Map<CommentType, string> = new Map([
    [CommentType.ANSWER, classes.commentAnwser]
  ]);

  const TypeColorScheme: Map<CommentType, string> = new Map([
    [CommentType.REQUEST_CLARIFICATION, '3'],
    [CommentType.COMMENT, '5'],
    [CommentType.ANSWER, '6'],
    [CommentType.CLARIFIED, '4'],
    [CommentType.HISTORY, '10']
  ]);

  const CommentTypeIcon: Map<CommentType, string> = new Map([
    [CommentType.REQUEST_CLARIFICATION, 'question-mark'],
    [CommentType.COMMENT, 'comment'],
    [CommentType.ANSWER, 'goal'],
    [CommentType.CLARIFIED, 'accept'],
    [CommentType.HISTORY, 'sys-find']
  ]);

  const sortByDate = (first: QueryComment, second: QueryComment) =>
    Date.parse(second.updatedAt) - Date.parse(first.updatedAt);

  let commentsSortedByDate: QueryComment[] = [];

  if (comments) {
    commentsSortedByDate = [...comments].sort(sortByDate);
  }

  const renderSupplier = (comment: QueryComment) => {
    if (!isInternalUser) return null;

    const supplier = comment.user.supplierLanguages;

    return !supplier || (!supplier.name && !supplier.code && !supplier.languages)
      ? renderSupplierSapCommenter()
      : renderSupplierExternalCommenter(supplier);
  };

  const renderSupplierSapCommenter = () => {
    return decorateSupplier(t('display-query-comments-sap'));
  };

  const renderSupplierExternalCommenter = (supplier: UserSupplierLanguages) => {
    if (!supplier.name || !supplier.code) return null;
    let supplierText = `${supplier?.name} "${supplier?.code}"`;

    const supplierLanguagesText = supplier?.languages.join(', ');
    if (supplierLanguagesText) supplierText = supplierText.concat(` - ${supplierLanguagesText}`);

    return decorateSupplier(supplierText);
  };

  const decorateSupplier = (supplier: string) => {
    return (
      <div data-testid="timeline-item-supplier" className={classes.supplier}>
        {`${supplier}` || ''}
      </div>
    );
  };

  return comments ? (
    <>
      {commentsSortedByDate?.map((comment) => {
        const userName = comment.user.displayName;
        const actionDate = getFormattedDateString(comment.updatedAt, true);

        return (
          <TimelineItem
            key={comment.id}
            icon={CommentTypeIcon.get(comment.commentType)}
            className={`${classes.comment} ${TimelineItemClass.get(comment.commentType) || ''}`}
            data-testid={`timeline-item-${comment.id}`}
          >
            <div className={classes.customTimelineItemDiv}>
              <div>
                <b data-testid="timeline-item-user">{userName}</b>
                <Tag
                  data-testid="timeline-item-badge"
                  className={classes.badge}
                  design={TagDesign.Set2}
                  colorScheme={TypeColorScheme.get(comment.commentType) || '5'}
                  wrappingType="None"
                >
                  {t(comment.commentType)}
                </Tag>
              </div>
              {renderSupplier(comment)}
              <div data-testid="timeline-item-date" className={classes.subTitleStyle}>
                {actionDate}
              </div>
              <CommentText
                comment={comment}
                maxCaracteres={NUM_MAX_CARACTERES}
                maxLines={NUM_MAX_LINES}
              />
            </div>
          </TimelineItem>
        );
      })}
    </>
  ) : null;
};
